<template>
  <section class="careerGuide">
    <EmptyStateNotice v-if="!isLoading && !hasAnyData" :text="t('careerGuide.emptyData')" />

    <template v-else-if="!isLoading">
      <CareerSectionPerformance v-if="sectionTable.length" :section-table="sectionTable" :graph-info="graphInfo" />

      <CareerClubAttendance
        v-if="performanceTable.length"
        :performance-table="performanceTable"
        :performance-info="performanceInfo"
      />

      <CareerEdarynSection v-if="edaryn.length" :edaryn="edaryn" :edaryn-info-image="edarynImage" />

      <CareerRecommendations v-if="recommendations" :recommendations="recommendations" />
    </template>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import CareerClubAttendance from '@/components/career-guide/CareerClubAttendance.vue';
import CareerEdarynSection from '@/components/career-guide/CareerEdarynSection.vue';
import CareerRecommendations from '@/components/career-guide/CareerRecommendations.vue';
import CareerSectionPerformance from '@/components/career-guide/CareerSectionPerformance.vue';
import EmptyStateNotice from '@/components/common/EmptyStateNotice.vue';
import edarynImage from '@/assets/career-guide/edaryn.png';
import {
  useCareerGuideEdarynQuery,
  useCareerGuidePerformanceQuery,
  useCareerGuideRecommendationsQuery,
  useCareerGuideSectionsQuery
} from '@/queries';
import { useLoadingStore } from '@/stores/loading';
import { useTopbarStore } from '@/stores/topbar';
import { useViewerStore } from '@/stores/viewer';

const { t, locale } = useI18n();
const topbarStore = useTopbarStore();
const loadingStore = useLoadingStore();
const viewerStore = useViewerStore();

const currentChildUin = computed(() => {
  const fromStore = viewerStore.currentChildUin;
  const fromStorage = localStorage.getItem('currentChildUin');
  return String(fromStore || fromStorage || '');
});

const performanceQuery = useCareerGuidePerformanceQuery(currentChildUin);
const sectionsQuery = useCareerGuideSectionsQuery(currentChildUin);
const edarynQuery = useCareerGuideEdarynQuery(currentChildUin);
const recommendationsQuery = useCareerGuideRecommendationsQuery(currentChildUin);

const sectionTable = computed(() => (Array.isArray(performanceQuery.data.value) ? performanceQuery.data.value : []));
const performanceTable = computed(() => (Array.isArray(sectionsQuery.data.value) ? sectionsQuery.data.value : []));
const edaryn = computed(() => (Array.isArray(edarynQuery.data.value) ? edarynQuery.data.value : []));
const recommendations = computed(() => recommendationsQuery.data.value || null);

const isLoading = computed(
  () =>
    performanceQuery.isLoading.value ||
    sectionsQuery.isLoading.value ||
    edarynQuery.isLoading.value ||
    recommendationsQuery.isLoading.value
);

const hasAnyData = computed(
  () =>
    sectionTable.value.length ||
    performanceTable.value.length ||
    edaryn.value.length ||
    hasRecommendations.value
);

const hasRecommendations = computed(() => {
  const data = recommendations.value || {};
  return (
    (Array.isArray(data.professions) && data.professions.length) ||
    (Array.isArray(data.institutions || data.education) && (data.institutions || data.education).length) ||
    (Array.isArray(data.firms || data.enterprises) && (data.firms || data.enterprises).length)
  );
});

const graphInfo = computed(() => [
  {
    color: '#F94144',
    backgroundColor: 'rgba(249, 65, 68, 0.2)',
    text: t('careerGuide.cycleLinguistic')
  },
  {
    color: '#F9C74F',
    backgroundColor: 'rgba(249, 199, 79, 0.2)',
    text: t('careerGuide.cycleHumanitarian')
  },
  {
    color: '#2D9CDB',
    backgroundColor: 'rgba(45, 156, 219, 0.2)',
    text: t('careerGuide.cycleNatural')
  },
  {
    color: '#43AA8B',
    backgroundColor: 'rgba(67, 170, 139, 0.2)',
    text: t('careerGuide.cyclePhysicalMath')
  }
]);

const performanceInfo = computed(() => [
  { color: '#6160DC', text: t('careerGuide.directionSport') },
  { color: '#54C5EB', text: t('careerGuide.directionEcoBio') },
  { color: '#24CCB8', text: t('careerGuide.directionScienceTech') },
  { color: '#FFB74A', text: t('careerGuide.directionTourism') },
  { color: '#FF823C', text: t('careerGuide.directionArt') },
  { color: '#FF4A55', text: t('careerGuide.directionNaturalMath') },
  { color: '#87220C', text: t('careerGuide.directionSocialPed') },
  { color: '#474A57', text: t('careerGuide.directionHumanitarian') }
]);

function applyTopbarTitle() {
  topbarStore.setTitle(t('career-guide'));
}

onMounted(() => {
  applyTopbarTitle();
});

watch(
  () => locale.value,
  () => {
    applyTopbarTitle();
  }
);

watch(
  () => isLoading.value,
  (value) => {
    loadingStore.set('career-guide-page', value);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  loadingStore.hide('career-guide-page');
});
</script>

<style scoped>
.careerGuide {
  padding-top: 32px;
  padding-inline: 24px;
  gap: 24px;
  display: flex;
  flex-direction: column;
  min-height: 160px;
}

.careerGuideTitle {
  font-family: Roboto, sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
  color: #18191f;
}

@media screen and (max-width: 768px) {
  .careerGuide {
    padding-inline: 16px;
    padding-top: 16px;
  }
}
</style>
